// Shared helpers for the shop: public API calls, visitor tracking, money and media URLs.
import { storeImageSrcset, storeImageUrl } from '../storeImages.js';
import { countStoreAction } from './visits.js';

// Calls /api<path>. Throws an Error carrying .status and .details (the host's extra information) when it fails.
export async function shopApi(path, options = {}) {
  const headers = new Headers(options.headers || {});
  // The anonymous visitor key rides along so the host can count unique shop views (page loads are GETs without a body).
  const visitor = visitorKey(); if (visitor && !headers.has('x-store-session')) headers.set('x-store-session', visitor);
  let body = options.body;
  if (body && !(body instanceof FormData)) { headers.set('content-type', 'application/json'); body = JSON.stringify(body); }
  const response = await fetch(`/api${path}`, { ...options, headers, body });
  if (!response.ok) {
    let payload = {};
    try { payload = await response.json(); } catch { payload.error = response.statusText; }
    const error = new Error((typeof payload.error === 'string' ? payload.error : payload.error?.message) || 'Request failed.');
    error.status = response.status; error.details = payload.details;
    throw error;
  }
  if (response.status === 204) return null;
  return (response.headers.get('content-type') || '').includes('application/json') ? response.json() : response;
}

function visitorKey() {
  try {
    const key = '3dlabs.publicSiteSession';
    let value = localStorage.getItem(key);
    if (!value) { value = crypto.randomUUID(); localStorage.setItem(key, value); }
    return value;
  } catch { return ''; }
}
// Anonymous shop analytics (page opens, product views, cart steps). Never blocks or throws.
// Counting never gets in the way: any failure is swallowed, and keepalive lets the count finish even when the tap
// that caused it has already moved the page on (a same-tab link to Instagram, say).
export function track(event_type, product_id = null, metadata = {}, payload = {}) {
  // A few safe actions also go to the visitor counter (visits.js decides which, and strips everything else).
  try { countStoreAction(event_type, metadata, payload); } catch { /* never blocks */ }
  try {
    shopApi('/public/store/events', { method: 'POST', keepalive: true, body: {
      event_type, product_id, metadata, session_key: visitorKey(), path: location.pathname,
      payload: { url: location.href, path: location.pathname, referrer: document.referrer || '', viewport: { width: window.innerWidth, height: window.innerHeight }, ...payload },
    } }).catch(() => {});
  } catch { /* never blocks */ }
}

export const money = (cents, currency = 'CAD') => new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency || 'CAD' }).format(Number(cents || 0) / 100);
// The price as shown on product pages and cards: whole dollars drop the ".00" ($45), anything else keeps its cents.
export const priceTag = (cents, currency = 'CAD') => new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency || 'CAD', minimumFractionDigits: Number(cents || 0) % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(Number(cents || 0) / 100);
// How many columns a grid of n products should use so that no row is left with a lone card.
export const gridCols = (count) => count <= 1 ? 1 : count === 2 || count === 4 ? 2 : 3;
export const isVideo = (path) => /(?:[?&]media=video|\.(?:webm|mp4|m4v|mov)(?:[?#]|$))/i.test(String(path || ''));
// A store photo at a fixed width (for CSS backgrounds and posters), or the path itself when it is not a store photo.
export const photoAt = (path, width) => storeImageUrl(path, width) || String(path || '');
export { storeImageSrcset, storeImageUrl };
// Product category as customers read it.
export const categoryName = (value) => /custom|universal/i.test(String(value || '')) ? 'Custom Fit' : String(value || 'Miata');
export const isCustom = (product) => categoryName(product?.category) === 'Custom Fit';
